'use client';

import Link from 'next/link';
import React from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useCustomerly } from 'react-live-chat-customerly';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  YoutubeIcon,
} from '@/components/common/SocialIcons';
import { footerColumns } from '@/content/navigation';
import { TRUST_BADGES } from '@/content/trust';
import { Chip, Container } from '@/components/ds';
import { cn } from '@/lib/twUtils';

const SOCIALS = [
  { href: 'https://www.linkedin.com/showcase/trovanorma/', label: 'LinkedIn', Icon: LinkedinIcon },
  { href: 'https://www.instagram.com/trovanorma_it/', label: 'Instagram', Icon: InstagramIcon },
  { href: 'https://www.facebook.com/trovanorma.it', label: 'Facebook', Icon: FacebookIcon },
  { href: 'https://www.youtube.com/@Trovanorma', label: 'YouTube', Icon: YoutubeIcon },
];

const LEGAL = [
  { href: '/chi-siamo/', label: 'Chi siamo' },
  { href: '/privacy/', label: 'Privacy Policy' },
  { href: '/cookie/', label: 'Cookie Policy' },
  { href: '/terms/', label: 'Termini e Condizioni' },
];

const linkClasses =
  'rounded-sm text-[0.90625rem] text-white/78 transition-colors hover:text-white ds-focus';

/** Voce della pagina in cui si è già: si vede prima di cliccarla. */
const currentClasses = 'text-white font-medium';

/**
 * Riapre il pannello delle preferenze della Cookie Solution di Iubenda, che
 * GTM carica su ogni pagina. La classe `iubenda-cs-preferences-link` è quella
 * che Iubenda aggancia da sé, ma solo agli elementi presenti al momento del
 * caricamento: con la navigazione client-side il piè di pagina viene
 * rimontato e l'aggancio si perde, quindi al click chiamiamo anche l'API.
 * Senza banner (tag disattivato, come in locale) il click non fa niente.
 */
const openCookiePreferences = () => {
  const iub = (window as unknown as { _iub?: { cs?: { api?: { openPreferences?: () => void } } } })
    ._iub;
  iub?.cs?.api?.openPreferences?.();
};

/**
 * Footer del sito istituzionale: colonne alimentate da content/navigation.ts.
 * Le pagine campagna continuano a usare Footer.tsx, senza link extra.
 */
const SiteFooter = () => {
  const { open } = useCustomerly();
  const pathname = usePathname();
  const trim = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);
  const isCurrent = (href: string) => trim(href) === trim(pathname);

  return (
    <footer className="relative overflow-hidden bg-navy-deep px-6 pt-18 pb-[var(--floating-cta-space,0px)] text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-30 h-[32.5rem] w-[32.5rem] rounded-full bg-[radial-gradient(circle,rgb(234_88_12/0.22),transparent_68%)]"
      />
      <Container bleed className="relative">
        <div className="flex flex-wrap justify-between gap-14">
          {/* Marchio */}
          <div className="max-w-[22.5rem] flex-1 basis-[18.75rem]">
            <Image
              src="/images/logo-horizontal-white.svg"
              alt="Trovanorma"
              width={215}
              height={27}
              className="h-[1.6875rem] w-auto"
            />
            <p className="mt-1.5 text-[0.71875rem] tracking-[0.02em] text-white/55">
              AI per professionisti legali, fiscali e del lavoro
            </p>
            <p className="mt-5 text-[0.9375rem] leading-[1.6] text-pretty text-white/66">
              Ricerca, analisi, redazione e revisione con fonti ufficiali sincronizzate ogni giorno
              e riferimenti disponibili per la verifica.
            </p>
            <div className="mt-6 flex gap-2.5">
              {SOCIALS.map(({ href, label, Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="grid h-[2.375rem] w-[2.375rem] place-items-center rounded-ds-sm border border-white/18 text-white/80 transition-colors hover:bg-white/10 hover:text-white ds-focus"
                >
                  <Icon className="h-[1.125rem] w-[1.125rem]" />
                </a>
              ))}
            </div>
          </div>

          {/* Colonne di link */}
          <div className="flex flex-wrap gap-x-16 gap-y-12">
            {footerColumns.map(column => (
              <nav key={column.title} className="flex min-w-[9.375rem] flex-col gap-3">
                <h2 className="text-[0.6875rem] font-semibold tracking-[0.14em] text-white/42 uppercase">
                  {column.title}
                </h2>
                {column.links.map(link => (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={isCurrent(link.href) ? 'page' : undefined}
                    className={cn(linkClasses, isCurrent(link.href) && currentClasses)}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            ))}
            <nav className="flex min-w-[9.375rem] flex-col gap-3">
              <h2 className="text-[0.6875rem] font-semibold tracking-[0.14em] text-white/42 uppercase">
                Azienda
              </h2>
              {LEGAL.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={isCurrent(link.href) ? 'page' : undefined}
                  className={cn(linkClasses, isCurrent(link.href) && currentClasses)}
                >
                  {link.label}
                </Link>
              ))}
              {/* L'unico modo per tornare sulla propria scelta sui cookie:
                  non va tolto senza mettere altro al suo posto. */}
              <button
                type="button"
                onClick={openCookiePreferences}
                className={`${linkClasses} iubenda-cs-preferences-link text-left`}
              >
                Preferenze cookie
              </button>
              <button type="button" onClick={() => open()} className={`${linkClasses} text-left`}>
                Contattaci
              </button>
            </nav>
          </div>
        </div>

        {/* Garanzie */}
        <ul className="mt-13 flex flex-wrap gap-2.5">
          {TRUST_BADGES.map(item => (
            <li key={item}>
              <Chip tone="onDark" dot className="text-white/72">
                {item}
              </Chip>
            </li>
          ))}
        </ul>

        {/* Dati societari e copyright */}
        <div className="mt-11 flex flex-wrap items-start justify-between gap-x-10 gap-y-5 border-t border-white/12 pt-6.5 pb-8.5">
          <p className="text-[0.78125rem] leading-[1.7] text-white/45">
            Endless Creativity S.r.l. · Via Alberico Albricci 8, 20122 Milano MI
            <br />
            P.IVA IT14287600960 · Cap. Soc. € 10.000,00 i.v. · REA MI-2772206
          </p>
          <p className="text-[0.78125rem] text-white/35">
            © {new Date().getFullYear()} Trovanorma. Tutti i diritti riservati.
          </p>
        </div>
      </Container>
    </footer>
  );
};

export default SiteFooter;
